import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import {
  useConnectMetaAds,
  useDisconnectMetaAds,
  useMetaAdsAuthorize,
} from '@/hooks/actions/use-meta-ads-actions';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type { MetaAdsMetadata } from '@fluenceur/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';
import { MetaAdsAdAccountList } from './MetaAdsAdAccountList';

interface MetaAdsIntegrationProps {
  id: string | null;
  isConnected: boolean;
  metadata?: MetaAdsMetadata;
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

// Routing prefix the backend stamps on the Business Login state, so this
// callback ignores the Instagram (Page token) callback that lands on the same
// integrations page.
const META_ADS_STATE_PREFIX = 'ma_';

/**
 * Meta Ads integration card. Connecting opens the ads Business Login dialog
 * where the brand grants ad-account access; the durable token is persisted
 * server-side as a separate `meta_ads` integration. This card only reflects
 * status and drives connect / disconnect; the ad-account picker stays in the
 * boost launch flow.
 */
const MetaAdsIntegration = ({
  id,
  isConnected,
  metadata,
  variant,
  detailHref,
}: MetaAdsIntegrationProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const brandId = user?.currentBrand?.id;

  // The connect leg navigates away on success, so the button must stay loading
  // through the redirect rather than snap back when the authorize mutation
  // settles. The disconnect leg is covered by the mutation's own `isPending`.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const authorize = useMetaAdsAuthorize();
  const connect = useConnectMetaAds();
  const disconnect = useDisconnectMetaAds();

  // Guards against processing the same callback twice (effect re-runs, StrictMode).
  const processedRef = useRef(false);

  const completeConnection = useCallback(
    (code: string, state: string) => {
      connect.mutate(
        { code, state },
        {
          onSuccess: () => {
            toaster.create({
              description: t('integrations.metaAds.success.connected'),
              type: 'success',
            });
          },
          onError: (error) => {
            toaster.create({
              description: getApiErrorMessage(
                error,
                t,
                'integrations.metaAds.errors.connectionFailed'
              ),
              type: 'error',
            });
          },
        }
      );
    },
    [connect, t]
  );

  useEffect(() => {
    if (processedRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state || !state.startsWith(META_ADS_STATE_PREFIX)) {
      return;
    }

    processedRef.current = true;
    completeConnection(code, state);

    // Clear the callback params so a reload does not replay the exchange.
    window.history.replaceState({}, '', window.location.pathname);
  }, [completeConnection]);

  const handleConnect = useCallback(async () => {
    if (!brandId) {
      toaster.create({ description: t('integrations.metaAds.errors.noBrand'), type: 'error' });
      return;
    }

    setIsRedirecting(true);
    try {
      const { redirectUrl } = await authorize.mutateAsync();
      toaster.create({ description: t('integrations.metaAds.redirecting'), type: 'loading' });
      window.location.href = redirectUrl;
    } catch (error) {
      toaster.create({
        description: getApiErrorMessage(error, t, 'integrations.metaAds.errors.connectionFailed'),
        type: 'error',
      });
      // On success the page redirects away; only a failure needs to clear this.
      setIsRedirecting(false);
    }
  }, [authorize, brandId, t]);

  const handleDisconnect = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      await disconnect.mutateAsync(id);
      toaster.create({
        description: t('integrations.metaAds.success.disconnected'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        description: getApiErrorMessage(error, t, 'integrations.metaAds.errors.disconnectFailed'),
        type: 'error',
      });
    }
  }, [disconnect, id, t]);

  const accounts = metadata?.adAccounts ?? [];
  const connectedAs =
    isConnected && accounts.length > 0
      ? t('integrations.metaAds.accountsConnected', { count: accounts.length })
      : undefined;

  return (
    <IntegrationCard
      name={t('integrations.metaAds.name')}
      description={t('integrations.metaAds.description')}
      icon="/images/integrations/meta.svg"
      iconStyle="mark"
      isActive={isConnected}
      isLoading={isRedirecting || connect.isPending || disconnect.isPending}
      disabled={!brandId || isRedirecting || connect.isPending || disconnect.isPending}
      onToggle={isConnected ? handleDisconnect : handleConnect}
      connectedAs={connectedAs}
      connectedLabel={t('integrations.metaAds.connectedLabel')}
      variant={variant}
      detailHref={detailHref}
    >
      {variant === 'detail' && isConnected && (
        <MetaAdsAdAccountList
          accounts={accounts}
          defaultAdAccountId={metadata?.defaultAdAccountId ?? null}
        />
      )}
    </IntegrationCard>
  );
};

export default MetaAdsIntegration;
